import { Facebook, Instagram, Mail, Twitter, Youtube } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/SmoothScroll";
import { useLocation, useNavigate } from "react-router-dom";
import { SITE } from "@/data/content";

const socials = [
  { key: "YouTube", url: SITE.youtube, Icon: Youtube, testId: "footer-social-youtube" },
  { key: "Instagram", url: SITE.instagram, Icon: Instagram, testId: "footer-social-instagram" },
  { key: "Facebook", url: SITE.facebook, Icon: Facebook, testId: "footer-social-facebook" },
  { key: "Twitter", url: SITE.twitter, Icon: Twitter, testId: "footer-social-twitter" },
].filter((s) => s.url);

export const Footer = () => {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const go = (id) => {
    const target = id === "contact" ? "about" : id; // contact details live in the About section
    return location.pathname === "/" ? scrollToId(target) : navigate(`/#${target}`);
  };
  const year = new Date().getFullYear();
  const watchLinks = t.nav.links.filter((l) => ["interviews", "ground-reports", "politics", "about"].includes(l.id));

  return (
    <footer data-testid="site-footer" className="border-t border-line bg-ink">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="flex items-baseline gap-1 font-display text-lg font-extrabold tracking-[0.18em] text-cream">
              {SITE.name}
              <span className="mb-0.5 inline-block h-1.5 w-1.5 bg-ember" aria-hidden="true" />
            </p>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-ash">{t.footer.description}</p>
            <div className="mt-7 flex items-center gap-3">
              {socials.map(({ key, url, Icon, testId }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId}
                  aria-label={key}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
              {SITE.email && (
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email-link"
                  aria-label="Email"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
                >
                  <Mail className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-ember">{t.footer.explore}</p>
            <ul className="mt-6 space-y-3">
              {t.nav.links.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => go(link.id)}
                    data-testid={`footer-link-${link.id}`}
                    className="text-sm text-ash transition-colors duration-200 hover:text-cream"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-ember">{t.footer.watch}</p>
            <ul className="mt-6 space-y-3">
              {watchLinks.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => go(link.id)}
                    data-testid={`footer-watch-${link.id}`}
                    className="text-sm text-ash transition-colors duration-200 hover:text-cream"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-2 text-sm text-ash" data-testid="footer-contact">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ember">{t.contact.collab}</p>
              <p>
                <a href={SITE.phoneHref} className="text-cream transition-colors duration-200 hover:text-ember" data-testid="footer-contact-phone">
                  {SITE.phone}
                </a>
              </p>
              <p>
                <a href={`mailto:${SITE.email}`} className="text-cream transition-colors duration-200 hover:text-ember" data-testid="footer-contact-email">
                  {SITE.email}
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-8">
          <p className="text-xs text-ash" data-testid="footer-copyright">
            © {year} {SITE.name}. {t.footer.rights}
          </p>
          <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-ash/60">
            ఇట్లు మీ జాఫర్
          </p>
        </div>
      </div>
    </footer>
  );
};
